import React, {FunctionComponent, MouseEventHandler, ReactNode, useCallback, useEffect, useMemo, useState} from "react";
import {
    Box,
    Breakpoint,
    Button,
    ButtonProps,
    Divider,
    IconButton,
    IconButtonProps,
    InputAdornment,
    inputBaseClasses,
    ListItemIcon,
    listItemIconClasses,
    ListItemText,
    listItemTextClasses,
    MenuItem,
    MenuItemProps,
    Popover,
    TextField,
    TextFieldProps,
    Theme,
    Tooltip,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {MoreVertRounded, Search} from "@mui/icons-material";
import useDebounced from "../../../hooks/use-debounced";
import {useUpdateEffect} from "react-use";
import UIUtils from "../../../utils";


const defaultBreakpoint: Breakpoint = 'sm';

export interface AppbarProps {
    startActionBreakpoint?: Breakpoint | number,
    endActionBreakpoint?: Breakpoint | number,
    startActions?: Array<AppbarStartActionProps & { key?: string }>,
    endActions?: Array<AppbarEndActionProps & { key?: string }>,
    searchbarProps?: Omit<AppbarSearchbarProps, 'variant'>,
    searchbarComponent?: ReactNode,
}

export const PanelAppbar: FunctionComponent<AppbarProps> = (props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement>();

    const open = useMemo(() => Boolean(anchorEl), [anchorEl]);
    const startActionBreakpoint = useMemo(() => props.startActionBreakpoint ?? defaultBreakpoint, [props.startActionBreakpoint]);
    const endActionBreakpoint = useMemo(() => props.endActionBreakpoint ?? defaultBreakpoint, [props.endActionBreakpoint]);

    const useMenuForStartActions = useMediaQuery<Theme>((theme) => theme.breakpoints.down(startActionBreakpoint))
    const useMenuForEndActions = useMediaQuery<Theme>((theme) => theme.breakpoints.down(endActionBreakpoint))

    const startActions = useMemo<Required<AppbarProps>['startActions']>(() => props.startActions?.map(e => ({
        ...e,
        ...(useMenuForStartActions && {
            sx: (theme) => ({
                '&:hover': {
                    backgroundColor: `rgba(${UIUtils.themeVar(`palette-${e.color ?? 'primary'}-mainChannel`)} / ${UIUtils.themeVar(`palette-action-selectedOpacity`)})`,
                    [`& .${listItemIconClasses.root}`]: {
                        color: theme.palette[e.color ?? 'primary'].main,
                    },
                    [`& .${listItemTextClasses.primary}`]: {
                        color: theme.palette[e.color ?? 'primary'].main,
                    },
                }
            }),
        })
    })) ?? [], [props.startActions, useMenuForStartActions]);

    const endActions = useMemo<Required<AppbarProps>['endActions']>(() => props.endActions?.map(e => ({
        ...e,
        ...(useMenuForEndActions && {
            sx: (theme) => ({
                '&:hover': {
                    backgroundColor: `rgba(${UIUtils.themeVar(`palette-${e.color ?? 'primary'}-mainChannel`)} / ${UIUtils.themeVar(`palette-action-selectedOpacity`)})`,
                    [`& .${listItemIconClasses.root}`]: {
                        color: theme.palette[e.color ?? 'primary'].main,
                    },
                    [`& .${listItemTextClasses.primary}`]: {
                        color: theme.palette[e.color ?? 'primary'].main,
                    },
                }
            }),
        })
    })) ?? [], [props.endActions, useMenuForEndActions]);

    /**
     * Opens the menu.
     * @param {MouseEvent<HTMLElement>} event    The event.
     */
    const openMenu = useCallback<MouseEventHandler<HTMLElement>>((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    /**
     * Closes the menu.
     */
    const closeMenu = useCallback<VoidCallback>(() => {
        setAnchorEl(undefined);
    }, []);

    /**
     * Closes the menu if it is open and the menu should not exist anymore.
     */
    useEffect(() => {
        if (!anchorEl)
            return;
        if (!useMenuForEndActions && !useMenuForStartActions) {
            closeMenu();
        }
    }, [useMenuForEndActions, useMenuForStartActions, anchorEl, closeMenu])

    /**
     * Closes the menu and calls the [action] callback.
     *
     * @param {MouseEventHandler<HTMLElement>} action    The action callback.
     * @return {MouseEventHandler<HTMLElement>}          The event handler.
     */
    const closeMenuOnActionSelection = useCallback<(action?: MouseEventHandler<HTMLElement>) => MouseEventHandler<HTMLElement>>(
        (action) => (...args) => {
            closeMenu();
            action?.(...args);
        }, [closeMenu])

    return (
        <>
            <Box display={'flex'} flexGrow={1} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-start'} gap={1}>
                    {
                        !useMenuForStartActions &&
                        startActions?.map((action) => (
                            <AppbarStartAction
                                breakpoint={startActionBreakpoint}
                                {...action}
                                anchorEl={anchorEl}
                                key={action.key}
                            />
                        ))
                    }
                </Box>
                <Box flexGrow={1}/>
                <Box display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={1}>
                    {
                        props.searchbarComponent ??
                        <AppbarSearchbar
                            placeholder={'search...'}
                            {...props.searchbarProps}
                        />
                    }
                    {
                        !useMenuForEndActions && (
                            endActions?.map((action, index, array) => (
                                <AppbarEndAction
                                    breakpoint={endActionBreakpoint}
                                    {...action}
                                    {...(index === array.length - 1 && {edge: 'end'})}
                                    anchorEl={anchorEl}
                                    key={action.key}
                                />
                            ))
                        )
                    }
                    {
                        (useMenuForEndActions || useMenuForStartActions) && (
                            <>
                                <Box sx={{position: 'relative',}}>
                                    <Tooltip title={"More Actions"}>
                                        <IconButton onClick={openMenu} edge={'end'}>
                                            <MoreVertRounded/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Popover
                                    id={'panel-appbar-actions-menu'}
                                    anchorEl={anchorEl}
                                    open={open}
                                    onClose={closeMenu}
                                    onClick={closeMenu}
                                    transformOrigin={{horizontal: 'right', vertical: 'top'}}
                                    anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                                    slotProps={{
                                        paper: {sx: {backgroundImage: 'none'}}
                                    }}
                                >
                                    {
                                        useMenuForStartActions &&
                                        startActions?.map((action) => (
                                            <AppbarStartAction
                                                breakpoint={startActionBreakpoint}
                                                {...action}
                                                key={action.key}
                                                anchorEl={anchorEl}
                                                onClick={closeMenuOnActionSelection(action.onClick)}
                                            />
                                        ))
                                    }
                                    {
                                        useMenuForEndActions && useMenuForStartActions &&
                                        <Divider/>
                                    }
                                    {
                                        useMenuForEndActions &&
                                        endActions?.map((action) => (
                                            <AppbarEndAction
                                                breakpoint={endActionBreakpoint}
                                                {...action}
                                                key={action.key}
                                                anchorEl={anchorEl}
                                                onClick={closeMenuOnActionSelection(action.onClick)}
                                            />
                                        ))
                                    }
                                </Popover>
                            </>
                        )
                    }
                    {
                        startActions.map(e => (
                            <React.Fragment key={e.key}>
                                {e.children}
                            </React.Fragment>
                        ))
                    }
                    {
                        endActions.map(e => (
                            <React.Fragment key={e.key}>
                                {e.children}
                            </React.Fragment>
                        ))
                    }
                </Box>
            </Box>
        </>
    );
}

export interface AppbarSearchbarProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
    value?: string,
    onChange?: (value: string) => void,
    timout?: number,
    onlyOnCommit?: boolean,
}

export const AppbarSearchbar: FunctionComponent<AppbarSearchbarProps> = (props) => {
    const {onlyOnCommit: ____, timout: ___, value: __, onChange: _, ...restProps} = props;
    const [value, setValue, debouncedValue, clearDebounceTimeout] = useDebounced(props.value ?? '', props.timout ?? 300);

    /**
     * With each change in the [value]:
     * - calls the [onChange] callback of the [props].
     */
    useUpdateEffect(() => {
        if (props.onlyOnCommit)
            return;
        props.onChange?.(debouncedValue);
    }, [props.onChange, debouncedValue, props.onlyOnCommit]);


    /**
     * Handle the change in the value by updating the [value].
     * @param {ChangeEvent<HTMLInputElement>} event   The event.
     */
    const onValueChanged = useCallback<Required<TextFieldProps>['onChange']>(
        (event) => setValue(event.target.value), [setValue]);

    /**
     * Search for the [value] by calling the [onChange] callback of the [props].
     */
    const search = useCallback<VoidCallback>(() => {
        props.onChange?.(value);
        clearDebounceTimeout();
    }, [props, value, clearDebounceTimeout]);

    /**
     * Handle the key down event by calling the [onChange] callback of the [props] when the [Enter] key is pressed.
     * @param {KeyboardEvent<HTMLDivElement>} event   The event.
     */
    const onKeyDown = useCallback<Required<TextFieldProps>['onKeyDown']>(
        (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                search();
            }
        }, [search]);

    /**
     * The styles applied to the search bar.
     *
     * @param {Theme} theme         The theme.
     * @return {ThemeMixin<Theme>}  The styles.
     */
    const mixin = useCallback<ThemeMixin<Theme>>((theme) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.vars?.shape.borderRadius,
        ...UIUtils.invokeSx<Theme>(props.sx, theme) as any,
        '& fieldset': {
            border: 'none',
        },
        [`& .${inputBaseClasses.root}`]: {
            paddingInlineEnd: theme.spacing(1),
        },
        [`& .${inputBaseClasses.input}`]: {
            fontSize: theme.typography.subtitle2.fontSize,
        }
    }), [props.sx])

    return (
        <>
            <TextField
                {...restProps}
                variant={'outlined'}
                onChange={onValueChanged}
                onKeyDown={onKeyDown}
                value={value}
                sx={mixin}
                size={'small'}
                InputProps={{
                    ...(restProps.InputProps ?? {}),
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton size={'small'} disableRipple color={'primary'} onClick={search}>
                                <Search fontSize={'small'}/>
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
        </>
    );
}

export interface AppbarStartActionProps extends Omit<IconButtonProps, 'color' | 'classes' | 'onClick'>, Omit<ButtonProps, 'color' | 'classes' | 'onClick'>, Omit<MenuItemProps<'button'>, 'color' | 'classes' | 'onClick'> {
    title: string,
    icon: ReactNode,
    buttonBreakpoint?: Breakpoint | number,
    breakpoint?: Breakpoint | number,
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    classes?: IconButtonProps['classes'] & ButtonProps['classes'] & MenuItemProps['classes'],
    onClick?: (e: React.MouseEvent<HTMLElement>, anchorEl?: HTMLElement | null) => void,
    anchorEl?: HTMLElement | null,
}

export const AppbarStartAction: FunctionComponent<AppbarStartActionProps> = (props) => {
    const {title, icon, breakpoint: _, buttonBreakpoint, onClick: propsOnClick, anchorEl, ...buttonProps} = props;


    const theme = useTheme();
    const breakpoint = useMemo(() => props.breakpoint ?? defaultBreakpoint, [props.breakpoint]);
    const useIconButton = useMediaQuery<Theme>((theme) => theme.breakpoints.between(breakpoint, buttonBreakpoint ?? breakpoint))
    const useMenuItem = useMediaQuery<Theme>((theme) => theme.breakpoints.down(breakpoint))

    /**
     * The event handler for the click event.
     */
    const onClick = useCallback<MouseEventHandler<HTMLElement>>((e) => {
        if (propsOnClick) {
            propsOnClick(e, props.anchorEl);
        }
    }, [propsOnClick, props.anchorEl])

    return (
        <>
            {
                !useIconButton && !useMenuItem && (
                    <Button {...buttonProps} onClick={onClick}>
                        {props.title}
                    </Button>
                )
            }
            {
                useIconButton && (
                    <Tooltip title={props.title}>
                        <span>
                            <IconButton {...buttonProps} onClick={onClick} color={'paper.'.concat(props.color ?? 'primary') as 'primary'}>
                                {props.icon}
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            }
            {
                useMenuItem && (
                    <MenuItem
                        {...buttonProps as MenuItemProps}
                        onClick={onClick}
                        sx={(theme) => ({
                            minHeight: `48px !important`,
                            ...(UIUtils.invokeSx<Theme>(props.sx, theme) as any),
                        })}
                    >
                        <ListItemIcon>
                            {props.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={props.title}
                            primaryTypographyProps={{
                                fontSize: theme.typography.subtitle2.fontSize,
                            }}
                        />
                    </MenuItem>
                )
            }
        </>
    );
}


export interface AppbarEndActionProps extends Omit<IconButtonProps, 'color' | 'classes' | 'onClick'>, Omit<MenuItemProps<'button'>, 'color' | 'classes' | 'onClick'> {
    title: string,
    icon: ReactNode,
    breakpoint?: Breakpoint | number;
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning',
    classes?: IconButtonProps['classes'] & MenuItemProps['classes'],
    onClick?: (e: React.MouseEvent<HTMLElement>, anchorEl?: HTMLElement | null) => void,
    anchorEl?: HTMLElement | null,
}

export const AppbarEndAction: FunctionComponent<AppbarEndActionProps> = (props) => {
    const {title, icon, breakpoint: _, onClick: propsOnClick, anchorEl, ...buttonProps} = props;

    const theme = useTheme();
    const breakpoint = useMemo(() => props.breakpoint ?? defaultBreakpoint, [props.breakpoint]);
    const useMenuItem = useMediaQuery<Theme>((theme) => theme.breakpoints.down(breakpoint))

    /**
     * The event handler for the click event.
     */
    const onClick = useCallback<MouseEventHandler<HTMLElement>>((e) => {
        if (propsOnClick) {
            propsOnClick(e, props.anchorEl);
        }
    }, [propsOnClick, props.anchorEl])

    return (
        <>
            {
                !useMenuItem && (
                    <Tooltip title={props.title}>
                        <span>
                            <IconButton {...buttonProps} onClick={onClick} color={'paper.'.concat(props.color ?? 'primary') as 'primary'}>
                                {props.icon}
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            }
            {
                useMenuItem && (
                    <MenuItem
                        {...buttonProps as MenuItemProps}
                        onClick={onClick}
                        sx={(theme) => ({
                            minHeight: `48px !important`,
                            ...(UIUtils.invokeSx<Theme>(props.sx, theme) as any),
                        })}
                    >
                        <ListItemIcon>
                            {props.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={props.title}
                            primaryTypographyProps={{
                                fontSize: theme.typography.subtitle2.fontSize,
                            }}
                        />
                    </MenuItem>
                )
            }
        </>
    );
}

export default PanelAppbar;
